import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { adminProductsApi } from "@/lib/commerce";
import { formatMoney } from "@/lib/format";

export default function AdminProducts() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [importState, setImportState] = useState(null);
  const [importError, setImportError] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkError, setBulkError] = useState("");
  const fileRef = useRef();

  const load = async () => {
    setBusy(true);
    try {
      const params = { page, page_size: 25 };
      if (q) params.q = q;
      if (statusFilter !== "all") params.status_filter = statusFilter;
      setData(await adminProductsApi.list(params));
      setSelected(new Set());
    } finally { setBusy(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [page, statusFilter]);

  const onSearch = (e) => { e.preventDefault(); setPage(1); load(); };

  const onPickFile = async (e) => {
    setImportError("");
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await adminProductsApi.importPreview(file);
      setImportState(res);
    } catch (err) {
      setImportError(err.response?.data?.error?.message || "Import failed.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const confirmImport = async () => {
    if (!importState) return;
    setBusy(true);
    try {
      const res = await adminProductsApi.importConfirm(importState.import_id);
      alert(`Imported ${res.inserted} new, ${res.updated} updated, ${res.skipped} skipped.`);
      setImportState(null);
      setPage(1);
      load();
    } catch (err) {
      setImportError(err.response?.data?.error?.message || "Confirm failed.");
    } finally { setBusy(false); }
  };

  const toggleOne = (id, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };
  const allChecked = data.items.length > 0 && data.items.every((p) => selected.has(p.id));
  const toggleAll = (checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const p of data.items) { if (checked) next.add(p.id); else next.delete(p.id); }
      return next;
    });
  };

  const bulkDelete = async () => {
    setBulkError("");
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(
      `Delete ${ids.length} product${ids.length > 1 ? "s" : ""}?\n\n` +
      "Products referenced by existing orders will be archived so past " +
      "orders and invoices keep working. Unreferenced products are removed " +
      "permanently.",
    )) return;
    setBusy(true);
    try {
      const res = await adminProductsApi.bulkDelete(ids);
      alert(res.message || "Done.");
      await load();
    } catch (err) {
      setBulkError(err.response?.data?.error?.message || "Bulk delete failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Admin › Products</div>
            <div className="text-lg font-semibold">Product catalogue</div>
          </div>
          <div className="flex items-center gap-3">
            <Button asChild variant="outline"><Link to="/admin">&larr; Admin</Link></Button>
            <Button asChild data-testid="admin-new-product"><Link to="/admin/products/new">Add product</Link></Button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-10 space-y-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Bulk import</CardTitle>
            <CardDescription>Upload CSV or XLSX with columns: <code>sku, name, price_aed</code> (required); optional: <code>slug, category_slug, brand, description, status, vat_rate, tax_inclusive, tax_code, image_url, is_featured</code>. Availability defaults to <b>Available</b>; toggle later on each product.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {importError && <Alert variant="destructive"><AlertDescription>{importError}</AlertDescription></Alert>}
            {!importState && (
              <input ref={fileRef} type="file" accept=".csv,.xlsx" onChange={onPickFile} data-testid="admin-import-file" />
            )}
            {importState && (
              <div className="space-y-3" data-testid="admin-import-preview">
                <div className="flex items-center gap-4 flex-wrap text-sm">
                  <Badge>Total: {importState.summary.total}</Badge>
                  <Badge variant="secondary">Valid: {importState.summary.valid}</Badge>
                  <Badge variant="destructive">Invalid: {importState.summary.invalid}</Badge>
                  {importState.preview_truncated && <span className="text-xs text-muted-foreground">Preview truncated to first 200 rows.</span>}
                </div>
                <div className="max-h-80 overflow-auto border rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-muted sticky top-0">
                      <tr><th className="p-2 text-left">Row</th><th className="p-2 text-left">SKU</th><th className="p-2 text-left">Name</th><th className="p-2 text-right">Price</th><th className="p-2 text-left">Errors</th></tr>
                    </thead>
                    <tbody>
                      {importState.preview.map((r) => (
                        <tr key={r.row} className={r.errors.length ? "bg-red-50" : ""}>
                          <td className="p-2">{r.row}</td>
                          <td className="p-2 font-mono">{r.sku}</td>
                          <td className="p-2">{r.name}</td>
                          <td className="p-2 text-right">{formatMoney({ amount_minor: r.price_minor, currency: "AED" })}</td>
                          <td className="p-2 text-destructive">{r.errors.join(", ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-3">
                  <Button onClick={confirmImport} disabled={busy || importState.summary.valid === 0} data-testid="admin-import-confirm">
                    Import {importState.summary.valid} valid rows
                  </Button>
                  <Button variant="ghost" onClick={() => setImportState(null)}>Cancel</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-3 items-center flex-wrap">
          <form onSubmit={onSearch} className="flex gap-2 flex-1 max-w-md">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search SKU / name / brand" data-testid="admin-products-search" />
            <Button type="submit" variant="secondary">Search</Button>
          </form>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="out_of_stock">Out of stock</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-sm text-muted-foreground">{data.total} products</div>
        </div>

        {selected.size > 0 && (
          <div className="flex items-center gap-3 bg-slate-950 text-white rounded-lg px-4 py-3" data-testid="admin-products-bulkbar">
            <div className="text-sm">
              <b>{selected.size}</b> selected
            </div>
            <div className="flex-1" />
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-white/10"
              onClick={() => setSelected(new Set())}
            >
              Clear
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={bulkDelete}
              disabled={busy}
              data-testid="admin-products-bulk-delete"
            >
              Delete selected
            </Button>
          </div>
        )}
        {bulkError && (
          <Alert variant="destructive"><AlertDescription>{bulkError}</AlertDescription></Alert>
        )}

        <div className="border rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="p-3 w-10">
                  <Checkbox
                    checked={allChecked}
                    onCheckedChange={(v) => toggleAll(!!v)}
                    aria-label="Select all"
                    data-testid="admin-products-select-all"
                  />
                </th>
                <th className="p-3 text-left">SKU</th>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Category</th>
                <th className="p-3 text-right">Price</th>
                <th className="p-3 text-left">Availability</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((p) => (
                <tr key={p.id} className="border-t" data-testid={`admin-product-row-${p.sku}`}>
                  <td className="p-3">
                    <Checkbox
                      checked={selected.has(p.id)}
                      onCheckedChange={(v) => toggleOne(p.id, !!v)}
                      aria-label={`Select ${p.name}`}
                      data-testid={`admin-product-select-${p.sku}`}
                    />
                  </td>
                  <td className="p-3 font-mono text-xs">{p.sku}</td>
                  <td className="p-3">{p.name}</td>
                  <td className="p-3 text-xs text-muted-foreground">{p.category_id ? "—" : ""}</td>
                  <td className="p-3 text-right">{formatMoney(p.price)}</td>
                  <td className="p-3">
                    <Badge
                      variant={p.is_stock_available ? "secondary" : "destructive"}
                      data-testid={`admin-product-avail-${p.sku}`}
                    >
                      {p.is_stock_available ? "Available" : "Not available"}
                    </Badge>
                  </td>
                  <td className="p-3"><Badge variant={p.status === "active" ? "secondary" : "outline"}>{p.status}</Badge></td>
                  <td className="p-3 text-right"><Button asChild size="sm" variant="ghost"><Link to={`/admin/products/${p.id}`}>Edit</Link></Button></td>
                </tr>
              ))}
              {data.items.length === 0 && !busy && (
                <tr><td colSpan="8" className="p-8 text-center text-sm text-muted-foreground">No products match.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {data.total > 25 && (
          <div className="flex justify-between items-center">
            <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <div className="text-sm text-muted-foreground">Page {page}</div>
            <Button variant="outline" disabled={page * 25 >= data.total} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        )}
      </main>
    </div>
  );
}
